import { type AnchorHTMLAttributes, forwardRef } from "react";
import {
  Link as BaseLink,
  LinkProps as BaseLinkProps,
  chakra,
  useRecipe,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { SITE_URL } from "@shared/env";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import type { LinkSharedProps } from "./Link.props";

const TransparentAnchor = chakra("a");
const TransparentNextLink = chakra(NextLink);

export type LinkProps = LinkSharedProps & Omit<BaseLinkProps, "target">;

function isExternalHref(href: string | undefined, external?: boolean): boolean {
  if (external !== undefined) return external;
  if (!href) return false;
  if (href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  if (href.startsWith("http://") || href.startsWith("https://")) {
    try {
      const url = new URL(href);
      const siteUrl = new URL(SITE_URL);
      return url.hostname !== siteUrl.hostname;
    } catch (e) {
      recordAppError(e);
      return true;
    }
  }
  return false;
}

function extractInternalPath(href: string): string {
  if (href.startsWith("http://") || href.startsWith("https://")) {
    try {
      const url = new URL(href);
      return url.pathname + url.search + url.hash;
    } catch (e) {
      recordAppError(e);
      return href;
    }
  }
  return href;
}

/**
 * Unified Link component for all navigation.
 *
 * Routing behavior:
 * - Internal same-domain hrefs use NextLink for client-side navigation.
 * - External hrefs (different hostname, mailto:, tel:) render a plain <a>.
 * - `external` prop forces the <a> path, skipping NextLink. Useful for
 *   same-domain URLs that are outside the Next.js app (e.g. /dashboard/).
 *
 * Target behavior:
 * - External links default to `target="_blank"` with `rel="noopener noreferrer"`.
 * - An explicit `target` prop always takes priority. Pass `target="_self"` to
 *   force same-tab navigation for external/non-Next.js URLs.
 * - `rel="noopener noreferrer"` is only added when the resolved target is "_blank".
 *
 * Widget / iframe usage:
 * - When a link needs to escape an iframe (`target="_top"`, `target="_parent"`),
 *   the call site must also pass `external` so NextLink is not used. NextLink
 *   intercepts clicks for client-side routing and ignores the target attribute.
 *
 * Styling modes:
 * - Default: renders Chakra BaseLink with the link recipe (color, underline, etc.).
 * - `asChild`: renders a transparent <a>/NextLink wrapper. The child component
 *   owns its own styling. The link recipe is not applied.
 */
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    children,
    size,
    variant = "plain",
    colorPalette = "primary",
    external,
    asChild: consumerAsChild,
    css,
    color,
    href,
    ...rest
  },
  ref
) {
  const recipe = useRecipe({ key: "link" });
  const recipeStyles = recipe({ variant });

  const isExternal = isExternalHref(href, external);

  const { target, rel, ...restProps } = rest;

  const sizeParams = () => {
    switch (size) {
      case "sm":
        return { fontSize: "14px" };
      case "md":
      default:
        return { fontSize: "16px" };
    }
  };

  const noRouting = !href || href === "#";
  const hasExplicitBlankTarget = target === "_blank";

  const styleProps = {
    ...sizeParams(),
    colorPalette,
    css: [recipeStyles, css],
    ...(color && { color }),
  };

  // When asChild is true, we render a plain <a> or <NextLink> instead of
  // Chakra's BaseLink. This prevents the link recipe styles (underline on
  // hover, display:inline, color override) from leaking onto the child
  // component (e.g. Button.Solid). The wrapper is visually transparent —
  // it only provides navigation, letting the child own its own styling.
  if (consumerAsChild) {
    const nativeProps = restProps as AnchorHTMLAttributes<HTMLAnchorElement>;

    if (noRouting) {
      return (
        <TransparentAnchor
          ref={ref}
          href={href}
          target={target}
          rel={rel}
          textDecoration="none"
          color="inherit"
          {...nativeProps}
        >
          {children}
        </TransparentAnchor>
      );
    }

    if (isExternal || hasExplicitBlankTarget) {
      const externalTarget = target ?? (isExternal ? "_blank" : undefined);
      const externalRel =
        externalTarget === "_blank" ? rel ?? "noopener noreferrer" : rel;
      return (
        <TransparentAnchor
          ref={ref}
          href={href}
          target={externalTarget}
          rel={externalRel}
          textDecoration="none"
          color="inherit"
          {...nativeProps}
        >
          {children}
        </TransparentAnchor>
      );
    }

    const resolvedPath = extractInternalPath(href);
    return (
      <TransparentNextLink
        ref={ref}
        href={resolvedPath}
        textDecoration="none"
        color="inherit"
        {...nativeProps}
      >
        {children}
      </TransparentNextLink>
    );
  }

  if (noRouting) {
    return (
      <BaseLink
        ref={ref}
        href={href}
        target={target}
        rel={rel}
        {...styleProps}
        {...restProps}
      >
        {children}
      </BaseLink>
    );
  }

  if (isExternal || hasExplicitBlankTarget) {
    const externalTarget = target ?? (isExternal ? "_blank" : undefined);
    const externalRel =
      externalTarget === "_blank" ? rel ?? "noopener noreferrer" : rel;
    return (
      <BaseLink
        ref={ref}
        href={href}
        target={externalTarget}
        rel={externalRel}
        {...styleProps}
        {...restProps}
      >
        {children}
      </BaseLink>
    );
  }

  const resolvedPath = extractInternalPath(href);
  return (
    <BaseLink asChild {...styleProps} {...restProps}>
      <NextLink ref={ref} href={resolvedPath}>
        {children}
      </NextLink>
    </BaseLink>
  );
});

export default Link;
